import { z } from "zod";
import { BillStatus } from "@common/types/bill";
import { accountOverridesRequestSchema } from "../accounting/account-override.schema";

const createBillLineItemSchema = z.object({
    description: z.string().max(1000).nullable(),
    amount: z.number().finite(),
    quantityDecimal: z.number().finite().nullable().optional(),
    unitAmount: z.number().finite().nullable().optional(),
    accountOverrides: accountOverridesRequestSchema.optional(),
    accountsPayableOverrides: accountOverridesRequestSchema.optional(),
    // Transitional legacy fields. New callers should use the
    // per-ledger override arrays; these resolve to the entity's
    // primary accrual ledger server-side.
    accountId: z.string().uuid().nullable().optional(),
    accountsPayableId: z.string().uuid().nullable().optional(),
    classIds: z.array(z.string().uuid()).max(20).optional(),
    tax: z.number().finite().nonnegative().optional(),
    discount: z.number().finite().nonnegative().optional(),
});

export type CreateBillLineItemBody = z.infer<typeof createBillLineItemSchema>;

export const createBillItemSchema = z.object({
    entityId: z.coerce.number().int(),
    financialAccountId: z.coerce.number().int().optional(),
    invoiceNumber: z.string().max(255).nullable(),
    counterpartyId: z.string().uuid().nullable().optional(),
    description: z.string().max(1000).nullable().optional(),
    due: z.coerce.date().nullable().optional(),
    createdAt: z.coerce.date().optional(),
    status: z.nativeEnum(BillStatus),
    memo: z.string().max(5000).nullable().optional(),
    currency: z
        .string()
        .regex(/^[a-zA-Z]{3}$/)
        .transform((v) => v.toLowerCase())
        .default("usd"),
    lines: z.array(createBillLineItemSchema).min(1).max(100),
    tax: z.number().finite().nonnegative().optional(),
    discount: z.number().finite().nonnegative().optional(),
    deposit: z.number().finite().nonnegative().optional(),
});

export type CreateBillItem = z.infer<typeof createBillItemSchema>;

export const createBillsBodySchema = z.object({
    bills: z.array(createBillItemSchema).min(1).max(50),
});

export type CreateBillsBody = z.infer<typeof createBillsBodySchema>;
